import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Divider, 
  Tabs, 
  Table, 
  Dropdown, 
  Menu, 
  Input, 
  message,
  Button,
  Space,
  Tooltip,
  Avatar,
  Progress,
  Statistic,
  Badge,
  Select,
  DatePicker,
  Modal,
  Form,
  Descriptions,
  Alert,
  Timeline,
  Rate
} from 'antd';
import { 
  EllipsisOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  DownloadOutlined,
  FilterOutlined,
  StarOutlined,
  MessageOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { getJobAssignments } from '../../api';
import './JobDetails.css';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;
const { confirm } = Modal;

const JobDetails = () => {
  const location = useLocation();
  const { job } = location.state;
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  useEffect(() => {
    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const response = await getJobAssignments(job._id);
        const jobCandidates = response.data;
        setCandidates(jobCandidates);
        setFilteredCandidates(jobCandidates);
      } catch (error) {
        message.error('Failed to fetch candidates');
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [job._id]);

  useEffect(() => {
    let filtered = candidates;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(candidate =>
        candidate.employee?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.employee?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.employee?.phone?.includes(searchTerm)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(candidate => candidate.status === statusFilter);
    }

    setFilteredCandidates(filtered);
  }, [candidates, searchTerm, statusFilter]);

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'orange',
      'reviewing': 'blue',
      'offer-sent': 'purple',
      'accepted': 'green',
      'rejected': 'red',
      'withdrawn': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusCount = (status) => {
    return candidates.filter(candidate => candidate.status === status).length;
  };



  const columns = [
    {
      title: 'Candidate',
      key: 'candidate',
      render: (record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />} 
            style={{ marginRight: '12px', backgroundColor: '#006971' }}
          />
          <div>
            <div style={{ fontWeight: '600', color: '#006971' }}>
              {record.employee?.fullName || 'N/A'}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.employee?.employeeId || 'N/A'}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <MailOutlined style={{ marginRight: '6px', color: '#666' }} />
            <Text style={{ fontSize: '13px' }}>{record.employee?.email || 'N/A'}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: '6px', color: '#666' }} />
            <Text style={{ fontSize: '13px' }}>{record.employee?.phone || 'N/A'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Application',
      key: 'application',
      render: (record) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Text strong style={{ fontSize: '12px' }}>Applied:</Text>
            <Text style={{ fontSize: '12px', marginLeft: '4px' }}>
              {new Date(record.createdAt).toLocaleDateString()}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Rate disabled defaultValue={4} size="small" style={{ fontSize: '12px' }} />
            <Text style={{ fontSize: '11px', marginLeft: '6px', color: '#666' }}>Match Score</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontWeight: '500' }}>
          {status?.toUpperCase() || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'View Details',
      key: 'view',
      render: (record) => (
        <Button 
          type="primary" 
          size="small"
          icon={<EyeOutlined />} 
                        onClick={() => navigate(`/employer/candidate-detail/${record._id}`, { state: { candidate: record } })}
        >
          View
        </Button>
      ),
    },
  ];

  const CandidateCard = ({ candidate }) => (
    <Card
      size="small"
      style={{ 
        marginBottom: '12px',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      hoverable
                  onClick={() => navigate(`/employer/candidate-detail/${candidate._id}`, { state: { candidate } })}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
        <Avatar 
          size={48} 
          icon={<UserOutlined />} 
          style={{ backgroundColor: '#006971', marginRight: '12px' }}
        />
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ margin: 0, color: '#006971' }}>
            {candidate.employee?.fullName || 'N/A'}
          </Title>
          <Tag color={getStatusColor(candidate.status)} size="small" style={{ marginTop: '4px' }}>
            {candidate.status?.toUpperCase() || 'PENDING'}
          </Tag>
        </div>
        <EyeOutlined style={{ color: '#006971', fontSize: '16px' }} />
      </div>
      
      <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
        <div style={{ marginBottom: '4px' }}>
          <MailOutlined style={{ marginRight: '6px', color: '#666' }} />
          {candidate.employee?.email || 'N/A'}
        </div>
        <div style={{ marginBottom: '4px' }}>
          <PhoneOutlined style={{ marginRight: '6px', color: '#666' }} />
          {candidate.employee?.phone || 'N/A'}
        </div>
        <div style={{ marginBottom: '8px' }}>
          <CalendarOutlined style={{ marginRight: '6px', color: '#666' }} />
          Applied: {new Date(candidate.createdAt).toLocaleDateString()}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Rate disabled defaultValue={4} size="small" style={{ fontSize: '10px' }} />
          </div>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            ID: {candidate.employee?.employeeId || 'N/A'}
          </Text>
        </div>
      </div>
    </Card>
  );



  return (
    <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/employer/job-management')}
              style={{ marginBottom: '16px' }}
            >
              Back to Assignments
            </Button>
            <Title level={2} style={{ margin: 0, color: '#006971' }}>
              {job.title}
            </Title>
            <div style={{ marginTop: '8px' }}>
              <Space size="middle">
                <Tag color={getStatusColor(job.status)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {job.status?.toUpperCase()}
                </Tag>
                <Text type="secondary">
                  <EnvironmentOutlined style={{ marginRight: '4px' }} />
                  {`${job?.address?.city}, ${job?.address?.state}`}
                </Text>
                <Text type="secondary">
                  <CalendarOutlined style={{ marginRight: '4px' }} />
                  {new Date(job.startDate).toLocaleDateString()}
                </Text>
              </Space>
            </div>
          </Col>
          <Col>
            <Space>
              <Button icon={<ShareAltOutlined />}>Share</Button>
              <Button icon={<EditOutlined />} onClick={() => navigate('/employer/post-job', { state: { job } })}>
                Edit Assignment
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

             {/* Job Information Tabs */}
       <Card style={{ marginBottom: '24px' }}>
         <Tabs defaultActiveKey="1">
           <TabPane tab="Assignment Details" key="1">
             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Assignment Information</Title>
               <Descriptions bordered size="small" column={2}>
                 <Descriptions.Item label="Code">{job.code}</Descriptions.Item>
                 <Descriptions.Item label="Department">{job.department}</Descriptions.Item>
                 <Descriptions.Item label="Manager">{job.managerName}</Descriptions.Item>
                 <Descriptions.Item label="Unit">{job.unit}</Descriptions.Item>
                 <Descriptions.Item label="Type">{job.type}</Descriptions.Item>
                 <Descriptions.Item label="Positions">{job.numberOfOpenings}</Descriptions.Item>
               </Descriptions>
             </div>

             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Compensation</Title>
               <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                 <Statistic
                   title="Base Rate"
                   value={job.salary?.base || 0}
                   prefix="$"
                   suffix="/hr"
                   valueStyle={{ fontSize: '16px', color: '#006971' }}
                 />
                 <Statistic
                   title="Overtime"
                   value={job.salary?.overtime || 0}
                   prefix="$"
                   suffix="/hr"
                   valueStyle={{ fontSize: '16px', color: '#006971' }}
                 />
               </div>
             </div>

             <div style={{ marginBottom: '24px' }}>
               <Title level={4}>Schedule</Title>
               <Descriptions bordered size="small" column={2}>
                 <Descriptions.Item label="Start Date">
                   {new Date(job.startDate).toLocaleDateString()}
                 </Descriptions.Item>
                 <Descriptions.Item label="End Date">
                   {new Date(job.endDate).toLocaleDateString()}
                 </Descriptions.Item>
                 <Descriptions.Item label="Hours/Day">{job.hoursPerDay}h</Descriptions.Item>
                 <Descriptions.Item label="Days/Week">{job.daysPerWeek}</Descriptions.Item>
                 <Descriptions.Item label="Shift">{job.shift}</Descriptions.Item>
                 <Descriptions.Item label="Float">{job.isFloat ? 'Yes' : 'No'}</Descriptions.Item>
               </Descriptions>
             </div>

             {job.mandatoryDocuments && job.mandatoryDocuments.length > 0 && (
               <div style={{ marginBottom: '24px' }}>
                 <Title level={4}>Required Documents</Title>
                 <div>
                   {job.mandatoryDocuments.map((doc, index) => (
                     <Tag key={index} style={{ margin: '2px' }}>
                       {doc.replace(/-/g, ' ')}
                     </Tag>
                   ))}
                 </div>
               </div>
             )}
           </TabPane>

           <TabPane tab="Job Description" key="2">
             <div style={{ padding: '16px 0' }}>
               <Title level={4}>Job Description</Title>
               <Paragraph>{job.duties}</Paragraph>
               
               {job.roles && (
                 <>
                   <Title level={4}>Roles and Responsibilities</Title>
                   <Paragraph>{job.roles}</Paragraph>
                 </>
               )}

               <Title level={4}>Requirements</Title>
               <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginTop: '16px' }}>
                 <div style={{ flex: 1, minWidth: '250px' }}>
                   <Text strong>Required Skills:</Text>
                   <div style={{ marginTop: '8px' }}>
                     {job.requiredSkills?.map((skill, index) => (
                       <Tag key={index} color="red" style={{ margin: '2px' }}>
                         {skill}
                       </Tag>
                     )) || <Text type="secondary">Not specified</Text>}
                   </div>
                 </div>
                 <div style={{ flex: 1, minWidth: '250px' }}>
                   <Text strong>Preferred Skills:</Text>
                   <div style={{ marginTop: '8px' }}>
                     {job.preferredSkills?.map((skill, index) => (
                       <Tag key={index} color="blue" style={{ margin: '2px' }}>
                         {skill}
                       </Tag>
                     )) || <Text type="secondary">Not specified</Text>}
                   </div>
                 </div>
               </div>
             </div>
           </TabPane>
         </Tabs>
       </Card>

       {/* Candidates Section - Outside of Tabs */}
       <Card title={
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           <Title level={3} style={{ margin: 0 }}>
             <TeamOutlined style={{ marginRight: '8px', color: '#006971' }} />
             Candidates ({candidates.length})
           </Title>
           <Button
             type={viewMode === 'table' ? 'primary' : 'default'}
             icon={<FilterOutlined />}
             onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
           >
             {viewMode === 'table' ? 'Card View' : 'Table View'}
           </Button>
         </div>
       }>
         {/* Candidate Statistics */}
         <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
           <Card size="small" style={{ flex: 1, minWidth: '120px', backgroundColor: '#f0f9ff' }}>
             <Statistic
               title="Total"
               value={candidates.length}
               valueStyle={{ color: '#006971', fontWeight: 'bold' }}
             />
           </Card>
           <Card size="small" style={{ flex: 1, minWidth: '120px', backgroundColor: '#fff7e6' }}>
             <Statistic
               title="Pending"
               value={getStatusCount('pending')}
               valueStyle={{ color: '#fa8c16', fontWeight: 'bold' }}
             />
           </Card>
           <Card size="small" style={{ flex: 1, minWidth: '120px', backgroundColor: '#f6ffed' }}>
             <Statistic
               title="Approved"
               value={getStatusCount('accepted')}
               valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
             />
           </Card>
           <Card size="small" style={{ flex: 1, minWidth: '120px', backgroundColor: '#f9f0ff' }}>
             <Statistic
               title="Offers Sent"
               value={getStatusCount('offer-sent')}
               valueStyle={{ color: '#722ed1', fontWeight: 'bold' }}
             />
           </Card>
         </div>

         {/* Filters and Controls */}
         <div style={{ 
           display: 'flex', 
           gap: '16px', 
           flexWrap: 'wrap', 
           marginBottom: '24px', 
           alignItems: 'end',
           padding: '16px',
           backgroundColor: '#fafafa',
           borderRadius: '8px'
         }}>
           <div style={{ flex: 2, minWidth: '200px' }}>
             <Text strong style={{ display: 'block', marginBottom: '4px' }}>Search</Text>
             <Search
               placeholder="Search by name, email, or phone..."
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               allowClear
               size="large"
             />
           </div>
           <div style={{ flex: 1, minWidth: '120px' }}>
             <Text strong style={{ display: 'block', marginBottom: '4px' }}>Status</Text>
             <Select
               value={statusFilter}
               onChange={setStatusFilter}
               style={{ width: '100%' }}
               size="large"
             >
               <Option value="all">All Status</Option>
               <Option value="pending">Pending</Option>
               <Option value="reviewing">Reviewing</Option>
               <Option value="offer-sent">Offer Sent</Option>
               <Option value="accepted">Accepted</Option>
               <Option value="rejected">Rejected</Option>
             </Select>
           </div>
         </div>

         {/* Candidates Display */}
         {viewMode === 'table' ? (
           <Table
             dataSource={filteredCandidates}
             columns={columns}
             loading={loading}
             pagination={{
               pageSize: 10,
               showSizeChanger: true,
               showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} candidates`,
               showQuickJumper: true
             }}
             scroll={{ x: 800 }}
             rowKey="_id"
           />
         ) : (
           <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
             {filteredCandidates.map(candidate => (
               <div key={candidate._id} style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                 <CandidateCard candidate={candidate} />
               </div>
             ))}
           </div>
         )}

         {filteredCandidates.length === 0 && !loading && (
           <div style={{ textAlign: 'center', padding: '48px' }}>
             <Text type="secondary" style={{ fontSize: '16px' }}>
               {searchTerm || statusFilter !== 'all' 
                 ? 'No candidates match your search criteria' 
                 : 'No candidates have applied for this position yet'
               }
             </Text>
           </div>
         )}
       </Card>

      
    </div>
  );
};

export default JobDetails;
